import { useCallback, useMemo } from 'react';

import APIHandler from 'config/APIHandler';

import { useChapters } from 'hooks/useChapters';
import { useUserInfo } from 'hooks/useUserInfo';

const defaultReturnData = {
  loaded: false,
  isAccessible: true,
  target: '',
  next: '',
  previous: '',
  update: () => {},
};

/**
 * @typedef {object} ProgressHandlerResults
 * @property {boolean} accessible - Indicates if the current page is accessible or not
 * @property {string} target - The target url if the page is not accessible
 * @property {string} next - The next page url
 * @property {string} previous - The previous page url
 * @property {Function} update - Updates the progress in the database
 */

/**
 * Custom hook to handle chapter- and page progress
 *
 * @param {number} chapterID - The id of the current chapter
 * @param {number} chapterPageNumber - The current page number. WARNING: the chapter page number is how it is registered
 *   in the backend (1-indexed), while the url is 0-indexed.
 * @param {string} pageType - The type of the pag (training, retain,)
 * @param {string} trainingType - The type of training (if applicable). At the moment it's only 'extra'. Used to insert
 *   a subdirectory in the url to maintain consistency between /training/chapterid and /training/extra/chapterid
 * @returns {ProgressHandlerResults} - An object containing the current chapter and page progress
 */
const useProgressHandler = (chapterID, chapterPageNumber, pageType = 'training', trainingType = '') => {
  const {
    user: { email },
    loaded: loadedUser,
  } = useUserInfo();
  const { chapters, loaded: loadedChapters } = useChapters();

  /**
   * @typedef {object} UpdateOptions
   * @property {number} progress - An optional override for the progress value. Will take precedence over both the
   *   default `nextPage` and `previousPage` behavior.
   * @property {number} lastPage - An optional override for the last page value. Will take precedence over both the
   *   default `nextPage` and `previousPage` behavior.
   * @property {boolean} previous - Shorthand for setting the progress update for navigation to the previous page.
   *   Defaults to false.
   */

  /**
   * Updates the progress int he database. Will include a few default assumptions, but can be overridden by the
   * parameters. The function will also include a few checks and balances to make sure that the values are within the
   * correct range.
   *
   * By default, the function assumes you are navigationg to the next page, but can be overridden.
   *
   * @param {UpdateOptions} options - The options object.
   */
  const update = useCallback(
    async ({ progress: progressOverride = null, lastPage: lastPageOverride = null, previous = false } = {}) => {
      const relevantChapter = chapters.find((possibleChapter) => Number(possibleChapter.id) === Number(chapterID));
      if (!relevantChapter) return;

      const { progress: currentProgress, chapterItemCount: totalChapterPages } = relevantChapter;

      const previousPageNumber = Number(chapterPageNumber) - 1;
      const nextPageNumber = Number(chapterPageNumber) + 1;

      // returns the input value, but limited by min/max constraint. (to make sure that no pages before 0 or after the max number of chapter pages are registered)
      // includes a few checks and balances for incorrect override values.
      const clamp = (value, min, max) => {
        const numValue = Number(value);
        if (Number.isNaN(numValue)) return min;
        return Math.min(Math.max(numValue, min), max);
      };

      const updateValues = {
        chapterId: Number(chapterID),
        email,
        badge: 'bronze'
      };

      if (progressOverride) {
        updateValues.progress = clamp(Number(progressOverride), Math.max(currentProgress, 0), totalChapterPages); // The minimum value
      } else {
        // If the current page is the second to last page, set the progress to the last page to take into account people closing the last page without clicking next (triggering the update).
        const newProgress = previous
          ? clamp(currentProgress, Math.max(currentProgress, 0), totalChapterPages) // When navigating to the prevous page, set progress to currently registered progress.
          : clamp(chapterPageNumber, Math.max(currentProgress, 0), totalChapterPages); // When navigating to the next page, set progress to the current page number
        updateValues.progress = newProgress + 1 === totalChapterPages ? totalChapterPages : newProgress;
      }

      if (lastPageOverride) {
        updateValues.lastPage = clamp(Number(lastPageOverride), 0, totalChapterPages);
      } else {
        updateValues.lastPage = previous
          ? clamp(previousPageNumber, 0, totalChapterPages)
          : clamp(nextPageNumber, 0, totalChapterPages);
      }

      const allValid = Object.values(updateValues).every((param) => param !== undefined && param !== null);

      if (allValid) {
        await APIHandler.setUserProgress(updateValues);
      }
    },
    [chapterID, chapterPageNumber, chapters, email],
  );

  const returnData = useMemo(() => {
    if (!loadedUser || !loadedChapters) return defaultReturnData;

    const chapter = chapters.find((possibleChapter) => Number(possibleChapter.id) === Number(chapterID));

    if (!chapter) return defaultReturnData;

    const { chapterItemCount: rawChapterItemCount, progress: rawChapterProgress } = chapter;
    const chapterItemCount = Number(rawChapterItemCount) ?? 0;
    const progress = Number(rawChapterProgress) ?? 0;

    const baseUrl = `/main/${pageType.toLowerCase()}${trainingType ? `/${trainingType}` : ''}/chapter/${chapterID}`;
    const collectionPageUrl = `/main/${pageType.toLowerCase()}`;

    const rootUrl = `${baseUrl}/${Math.max(progress, 0)}`; // first page of the chapter is always index 0. Otherwise will redirect to the first uncompleted page.
    const nextUrl = `${baseUrl}/${chapterPageNumber}`; // Uses the chapterPageNumber since the url is index 0 based. Meaning the current index is -1, the next is -0
    const previousUrl = `${baseUrl}/${chapterPageNumber - 2}`; // uses chapterPageNumber -2 since the url is index 0 based. Meaning current is -1, previous is -2

    const isAccessible = progress >= chapterPageNumber - 2; // TODO: includes a margin of error [2 pages instead of 1] to account for delays in the updating process. Should look into a better solution.
    const target = rootUrl;
    const next = chapterPageNumber + 1 <= chapterItemCount ? nextUrl : collectionPageUrl;
    const previous = chapterPageNumber - 1 > 0 ? previousUrl : collectionPageUrl;

    return {
      loaded: loadedUser && loadedChapters,
      isAccessible,
      target,
      next,
      previous,
      update,
    };
  }, [loadedUser, loadedChapters, chapters, pageType, trainingType, chapterID, chapterPageNumber, update]);

  return returnData;
};

export { useProgressHandler };
